import { analyserMontant } from './montant'

// Libellés bancaires : forme normalisée (unicité des lignes de relevé, recherche),
// repérage des rejets de prélèvement et lecture des montants en devise.

export function normaliserLibelle(libelle: string): string {
  return libelle
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u2013\u2014]/g, '-')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim()
}

const REJET = /\b(REJET|REJETE|IMPAYE|RETOUR PRLV|RETOUR PRELEVEMENT|PRLV REJETE)\b/

/** Libellé (normalisé) d'un rejet ou d'un impayé : REJET, IMPAYE, RETOUR PRLV. */
export function estRejet(libelleNormalise: string): boolean {
  return REJET.test(libelleNormalise)
}

/** Le libellé contient-il ce numéro (facture, référence), séparateurs ignorés ? Au moins 4 caractères. */
export function contientNumero(libelleNormalise: string, numero: string): boolean {
  const compact = (t: string) => normaliserLibelle(t).replace(/[^A-Z0-9]/g, ' ')
  const cible = compact(numero).replace(/ /g, '')
  if (cible.length < 4) return false
  const mots = compact(libelleNormalise).split(' ').filter(Boolean)
  // Le numéro peut être découpé en plusieurs mots consécutifs (F2026 0042).
  for (let i = 0; i < mots.length; i++) {
    let assemble = ''
    for (let j = i; j < mots.length && assemble.length < cible.length; j++) {
      assemble += mots[j]
      if (assemble === cible) return true
    }
  }
  return false
}

/**
 * Codes de devise (ISO 4217) lus dans un libellé : l'euro et les devises usuelles d'une dépense
 * hors zone euro. Pas les codes qui sont aussi des mots (ALL, MAD, PEN, TRY…) : pris pour une
 * devise, un mot brouillerait la lecture. Un code manque : l'ajouter ici.
 */
export const DEVISES_LIBELLE: readonly string[] = [
  'AED',
  'AUD',
  'BRL',
  'CAD',
  'CHF',
  'CNY',
  'CZK',
  'DKK',
  'EUR',
  'GBP',
  'HKD',
  'HUF',
  'INR',
  'ISK',
  'JPY',
  'KRW',
  'MXN',
  'NOK',
  'NZD',
  'PLN',
  'RON',
  'SEK',
  'SGD',
  'THB',
  'TND',
  'USD',
  'XAF',
  'XOF',
  'XPF',
  'ZAR',
]

/** Montant cité par un libellé, avec sa devise ; sans signe : le sens est celui de l'opération. */
export type MontantEnDevise = { montantCts: number; devise: string }

/**
 * Jetons d'un libellé normalisé : montant à deux décimales, milliers groupés par un point ou une
 * virgule (« 1,234.56 », « 1.234,56 ») ou non (« 1234,56 »), sans chiffre derrière ; code de
 * devise de trois lettres, sans lettre derrière ; signe de l'euro. Ce qui précède est vérifié à
 * part (pas de regard en arrière : le web charge aussi ce module).
 */
const JETON =
  /(\d{1,3}(?:,\d{3})+\.\d{2}|\d{1,3}(?:\.\d{3})+,\d{2}|\d+[.,]\d{2})(?![.,]?\d)|([A-Z]{3})(?![A-Z])|(€)/g
/** Un montant n'est pas la suite d'un nombre ou d'une référence (« F2026.12 », « 1.234 »). */
const AVANT_MONTANT_INTERDIT = /[\dA-Z.,]/
/** Un code de devise n'est pas la fin d'un mot (« EURL »). */
const AVANT_CODE_INTERDIT = /[A-Z]/
/** Trois chiffres après ce nombre et une espace : un groupe de milliers, ou un autre nombre ? */
const GROUPE_AVANT = /(?:^|[^\dA-Z.,])\d{1,3}(?: \d{3})* $/

type Jeton = { debut: number; fin: number; montantCts: number | null; devise: string | null }

function jetonsDe(t: string): Jeton[] {
  const jetons: Jeton[] = []
  for (const m of t.matchAll(JETON)) {
    const [brut, montant, code, euro] = m
    const debut = m.index
    const fin = debut + brut.length
    const avant = t[debut - 1] ?? ''
    if (montant !== undefined) {
      if (AVANT_MONTANT_INTERDIT.test(avant)) continue
      // « 1 234,56 USD » ou « 12/10 400,00 USD » : milliers groupés par une espace, ou une date
      // avant le montant ? On ne sait pas : pas lu.
      if (/^\d{3}[.,]\d{2}$/.test(montant) && GROUPE_AVANT.test(t.slice(0, debut))) continue
      // Partie entière sans ses séparateurs de milliers, puis les deux décimales.
      const entiere = montant.slice(0, -3).replace(/[.,]/g, '')
      const montantCts = analyserMontant(`${entiere}.${montant.slice(-2)}`)
      if (montantCts !== null) jetons.push({ debut, fin, montantCts, devise: null })
    } else if (euro !== undefined) {
      jetons.push({ debut, fin, montantCts: null, devise: 'EUR' })
    } else if (
      code !== undefined &&
      !AVANT_CODE_INTERDIT.test(avant) &&
      DEVISES_LIBELLE.includes(code)
    ) {
      jetons.push({ debut, fin, montantCts: null, devise: code })
    }
  }
  return jetons
}

/**
 * Montants en devise que cite un libellé bancaire, le montant d'origine d'un paiement par carte
 * surtout : « 40,00 USD », « 40.00 USD », « USD 40.00 ». Dans l'ordre, sans doublon. Un montant
 * va avec le code qui le touche (rien que des espaces entre eux). Ce qui est ambigu n'est pas
 * lu : une devise entre deux montants, un montant entre deux devises (« 40,00 USD 35,67 »), des
 * milliers groupés par une espace. Ni taux à plus de deux décimales, ni symbole autre que €.
 */
export function montantsEnDevise(texte: string): MontantEnDevise[] {
  const t = normaliserLibelle(texte)
  const lus: MontantEnDevise[] = []
  // Chaîne de jetons qui se touchent, montant et devise en alternance. Paire, chaque montant va
  // avec son voisin (« 31,50 USD 28,07 EUR », « USD 31.50 EUR 28.07 ») ; impaire, on ne sait pas
  // lequel : rien.
  let chaine: Jeton[] = []
  const conclure = () => {
    if (chaine.length % 2 === 0) {
      for (let i = 0; i + 1 < chaine.length; i += 2) {
        const [a, b] = [chaine[i]!, chaine[i + 1]!]
        const montantCts = a.montantCts ?? b.montantCts
        const devise = a.devise ?? b.devise
        if (montantCts === null || devise === null) continue
        if (!lus.some((l) => l.montantCts === montantCts && l.devise === devise))
          lus.push({ montantCts, devise })
      }
    }
    chaine = []
  }
  for (const j of jetonsDe(t)) {
    const prec = chaine.at(-1)
    const touche =
      prec !== undefined &&
      (prec.devise === null) !== (j.devise === null) &&
      t.slice(prec.fin, j.debut).trim() === ''
    if (!touche) conclure()
    chaine.push(j)
  }
  conclure()
  return lus
}
